import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import AuthPage from "./components/AuthPage";
import WorkspacePage from "./components/WorkspacePage";
import EmailVerificationPage from "./components/EmailVerificationPage"; // New import
import DevModeDashboard from "./components/DevModeDashboard";
import { Toaster } from "./components/ui/sonner";
import { DBProvider } from "./context/DBProvider";
import { ThemeProvider } from "./context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/v2`;

// Axios interceptor to add token to requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem("token");
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900 text-white">
        <div className="text-lg text-slate-300">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <DBProvider>
            <Routes>
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate to="/workspace" replace />
                  ) : (
                    <Navigate to="/auth" replace />
                  )
                }
              />
              <Route
                path="/auth"
                element={
                  isAuthenticated ? (
                    <Navigate to="/workspace" replace />
                  ) : (
                    <AuthPage onLogin={handleLogin} />
                  )
                }
              />
              <Route
                path="/workspace"
                element={
                  isAuthenticated ? (
                    <WorkspacePage user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to="/auth" replace />
                  )
                }
              />
              <Route path="/verify-email" element={<EmailVerificationPage />} /> {/* New route */}
              <Route
                path="/dev"
                element={
                  isAuthenticated && user?.is_staff ? (
                    <DevModeDashboard user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to="/workspace" replace />
                  )
                }
              />
              <Route
                path="/dev-mode"
                element={<Navigate to="/dev" replace />}
              />
            </Routes>
          </DBProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
